import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';

const SYMBOL_PATTERN = /[♥💘]/;
const DEFAULT_LIMIT = 8;
const SHORT_LIMIT = 3;
const TRADITIONAL_STYLE = 'traditional';

/**
 * @typedef {object} EnhancementRowsRefs
 * @property {HTMLElement} [summary] - The footer line, hidden while nothing is chosen.
 * @property {HTMLElement} [summaryNames] - The footer line naming the chosen additions.
 * @property {HTMLElement} [summaryTotal] - The footer line carrying their combined price.
 */

/**
 * Optional additions on the product form. Each `.enhancement-row` holds one checkbox named
 * `properties[_enhancement_{CODE}]`; its option inputs stay disabled until the row is chosen so an
 * unchosen addition submits nothing. All state is read from the DOM at use time because combined
 * listings morph the product form without a page load.
 *
 * @extends Component<EnhancementRowsRefs>
 */
class EnhancementRowsComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    for (const row of this.#rows) this.#syncRow(row);
    this.#updateSummary();
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.closest('.shopify-section')?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Copies the glove colour from the re-rendered block when the variant changes without a page load.
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        const next = detail?.html?.querySelector('enhancement-rows-component');
        if (!next) return;

        this.classList.toggle('enhancement-rows--glove', next.classList.contains('enhancement-rows--glove'));
        this.classList.toggle('enhancement-rows--glove-dark', next.classList.contains('enhancement-rows--glove-dark'));
        const glove = next.style.getPropertyValue('--enhancement-glove');
        if (glove) {
          this.style.setProperty('--enhancement-glove', glove);
        } else {
          this.style.removeProperty('--enhancement-glove');
        }
      })
      .catch(() => {});
  };

  updatedCallback() {
    super.updatedCallback();
    for (const row of this.#rows) this.#syncRow(row);
    this.#updateSummary();
  }

  /**
   * Chooses or clears an addition.
   * @param {Event} event
   */
  toggle(event) {
    const row = this.#rowFor(event.target);
    if (!row) return;

    this.#syncRow(row);
    this.#updateSummary();
  }

  /**
   * Re-applies the character limit when a detailing option (for instance the monogram style) changes.
   * @param {Event} event
   */
  handleStyle(event) {
    const row = this.#rowFor(event.target);
    if (!row) return;

    this.#applyCharacterLimit(row);
    this.#updatePreview(row);
  }

  /**
   * Recolours the preview when a thread colour is chosen.
   * @param {Event} event
   */
  handleColour(event) {
    const row = this.#rowFor(event.target);
    if (!row) return;

    this.#updatePreview(row);
  }

  /**
   * Enforces the character limit while typing and refreshes the preview.
   * @param {Event} event
   */
  handleCharacters(event) {
    const row = this.#rowFor(event.target);
    if (!row) return;

    this.#applyCharacterLimit(row);
    this.#updatePreview(row);
  }

  /**
   * Inserts a symbol at the caret of the characters field.
   * @param {Event} event
   */
  insertSymbol(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-symbol]') : null;
    const row = this.#rowFor(button);
    const input = this.#charactersInput(row);
    const symbol = button instanceof HTMLElement ? button.dataset.symbol : undefined;
    if (!row || !input || !symbol) return;

    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    input.value = input.value.slice(0, start) + symbol + input.value.slice(end);

    const caret = start + symbol.length;
    input.setSelectionRange(caret, caret);
    input.focus();

    this.#applyCharacterLimit(row);
    this.#updatePreview(row);
  }

  /** @returns {HTMLElement[]} */
  get #rows() {
    return Array.from(this.querySelectorAll('.enhancement-row'));
  }

  /**
   * @param {EventTarget | Element | null | undefined} target
   * @returns {HTMLElement | null}
   */
  #rowFor(target) {
    if (!(target instanceof Element)) return null;
    const row = target.closest('.enhancement-row');
    return row instanceof HTMLElement ? row : null;
  }

  /**
   * @param {HTMLElement} row
   * @returns {HTMLInputElement | null}
   */
  #toggleInput(row) {
    const input = row.querySelector('.enhancement-row__add-input');
    return input instanceof HTMLInputElement ? input : null;
  }

  /**
   * @param {HTMLElement | null} row
   * @returns {HTMLInputElement | null}
   */
  #charactersInput(row) {
    const input = row?.querySelector('[data-characters]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /**
   * Enables the option inputs of a chosen row and disables (and resets) those of a cleared one.
   * @param {HTMLElement} row
   */
  #syncRow(row) {
    const chosen = this.#toggleInput(row)?.checked ?? false;
    row.toggleAttribute('data-chosen', chosen);

    for (const option of row.querySelectorAll('[data-enhancement-option]')) {
      if (!(option instanceof HTMLInputElement || option instanceof HTMLTextAreaElement)) continue;

      option.disabled = !chosen;
      if (chosen) continue;

      if (option instanceof HTMLInputElement && option.type === 'radio') {
        option.checked = option.defaultChecked;
      } else {
        option.value = '';
      }
    }

    this.#applyCharacterLimit(row);
    this.#updatePreview(row);
  }

  /**
   * Traditional monograms and any entry holding a symbol are capped at three characters; otherwise
   * eight, with full stops not counted.
   * @param {HTMLElement} row
   */
  #applyCharacterLimit(row) {
    const input = this.#charactersInput(row);
    if (!input) return;

    const style = row.querySelector('[data-character-style]:checked');
    const traditional = style instanceof HTMLInputElement && style.value.toLowerCase() === TRADITIONAL_STYLE;
    const limit = traditional || SYMBOL_PATTERN.test(input.value) ? SHORT_LIMIT : DEFAULT_LIMIT;

    input.maxLength = traditional ? SHORT_LIMIT : DEFAULT_LIMIT;

    if (traditional) {
      if (input.value.length > limit) input.value = input.value.slice(0, limit);
    } else {
      while (countCharacters(input.value) > limit) {
        input.value = Array.from(input.value).slice(0, -1).join('');
      }
    }
  }

  /**
   * Writes the preview text. A traditional monogram embroiders its middle initial larger than the
   * others, so with an odd number of letters the centre one is wrapped for emphasis. Full stops are
   * skipped when finding the centre.
   * @param {HTMLElement} preview
   * @param {string} value
   * @param {boolean} traditional
   */
  #renderPreviewText(preview, value, traditional) {
    if (!value) {
      preview.textContent = preview.dataset.placeholder || '';
      return;
    }

    const characters = Array.from(value);
    const letterIndexes = characters.flatMap((character, index) => (character === '.' ? [] : [index]));
    const middle = traditional && letterIndexes.length % 2 === 1 ? letterIndexes[(letterIndexes.length - 1) / 2] : -1;

    if (middle < 0) {
      preview.textContent = value;
      return;
    }

    const emphasis = document.createElement('span');
    emphasis.className = 'enhancement-row__preview-middle';
    emphasis.textContent = characters[middle];
    preview.replaceChildren(characters.slice(0, middle).join(''), emphasis, characters.slice(middle + 1).join(''));
  }

  /**
   * Mirrors the characters field into the preview, styled by the chosen monogram style.
   * @param {HTMLElement} row
   */
  #updatePreview(row) {
    const input = this.#charactersInput(row);
    const preview = row.querySelector('[data-preview]');
    if (!input || !(preview instanceof HTMLElement)) return;

    const style = row.querySelector('[data-character-style]:checked');
    const traditional = style instanceof HTMLInputElement && style.value.toLowerCase() === TRADITIONAL_STYLE;

    const colour = row.querySelector('[data-colour]:checked');
    const colourValue = colour instanceof HTMLInputElement ? (colour.dataset.colour ?? '') : '';

    this.#renderPreviewText(preview, input.value, traditional);
    preview.classList.toggle('enhancement-row__preview--traditional', traditional);
    preview.classList.toggle('enhancement-row__preview--empty', input.value === '');

    if (colourValue) {
      preview.style.setProperty('--enhancement-preview-colour', colourValue);
    } else {
      preview.style.removeProperty('--enhancement-preview-colour');
    }
  }

  #updateSummary() {
    const { summary, summaryNames, summaryTotal } = this.refs;
    if (!summary || !summaryNames || !summaryTotal) return;

    const chosen = this.#rows.filter((row) => this.#toggleInput(row)?.checked);
    const names = chosen.map((row) => row.dataset.name ?? '').filter(Boolean);
    const total = chosen.reduce((sum, row) => sum + Number(row.dataset.price ?? 0), 0);

    summary.hidden = chosen.length === 0;
    if (chosen.length === 0) {
      summaryNames.textContent = '';
      summaryTotal.textContent = '';
      return;
    }

    const suffix = this.dataset.summarySuffix ? ` · ${this.dataset.summarySuffix}` : '';
    summaryNames.textContent = `${names.join(', ')}${suffix}`;

    const format = this.dataset.moneyFormat;
    const currency = this.dataset.currency;
    summaryTotal.textContent = format && currency ? `+ ${formatMoney(total, format, currency)}` : '';
  }
}

/**
 * Counts characters excluding full stops. The spread keeps surrogate pairs (💘) as one character.
 * @param {string} value
 * @returns {number}
 */
function countCharacters(value) {
  return Array.from(value.replace(/\./g, '')).length;
}

if (!customElements.get('enhancement-rows-component')) {
  customElements.define('enhancement-rows-component', EnhancementRowsComponent);
}
